/**
 * Publish a recall notice — `/recalls/new`, `screens.md` §11, plan U48.
 *
 * Three steps, a confirmation and the live state, all inside `RecallFlow` so a `PlanGate`
 * anywhere below throws. `?product=` opens at step 2. Decisions live in `recall-flow.ts`.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import {
  recallNoticePath,
  type BatchSummary,
  type ProductSummary,
  type RecallSummary,
} from "@packagepage/shared";
import { Button, Input, RecallFlow, Surface } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { displayName } from "./product-row.js";
import { SEARCH_DEBOUNCE_MS } from "./ProductList.js";
import {
  CARVE_OUT,
  EMPTY_RECALL_FORM,
  EXACT_MATCH_WARNING,
  NEW_TO_US,
  NOT_FOUND_MECHANISM,
  NOT_SURE,
  NOTICE_FIELDS,
  OVERRIDE_LABEL,
  OVERRIDE_OFF,
  OVERRIDE_ON,
  PUBLISH_LABEL,
  SCOPE_OF_SERVICE,
  affectedProducts,
  lotsNotOnFile,
  noticeAddress,
  preflight,
  publishOutcome,
  refusalView,
  scopeSentence,
  splitLots,
  toPublishBody,
  type PublishOutcome,
  type RecallForm,
} from "./recall-flow.js";

import "@packagepage/ui/input.css";
import "@packagepage/ui/button.css";
import "./product-list.css";
import "./recall-flow.css";

type Step = "product" | "scope" | "notice" | "confirm" | "live";

interface Chosen {
  readonly id: string;
  readonly gtin14: string;
  readonly name: string;
  readonly status: ProductSummary["status"];
}

function chosenOf(product: ProductSummary): Chosen {
  return {
    id: product.id,
    gtin14: product.gtin14,
    name: displayName(product.name, product.gtin14).text,
    status: product.status,
  };
}

function activeRecallOn(
  recalls: readonly RecallSummary[],
  productId: string,
): RecallSummary | undefined {
  return recalls.find((r) => r.productId === productId && r.status === "active");
}

function StepHeader({ step, children }: { step: string; children: ReactNode }) {
  return (
    <div className="pp-recall-flow__head">
      <div className="pp-recall-flow__title">{children}</div>
      <div className="pp-recall-flow__step">{step}</div>
    </div>
  );
}

function ProductLine({ product, onChange }: { product: Chosen; onChange?: () => void }) {
  return (
    <div className="pp-recall-flow__product">
      <span>{product.name}</span>
      <span className="pp-recall-flow__code">{product.gtin14}</span>
      {onChange === undefined ? null : (
        <button type="button" className="pp-recall-flow__link" onClick={onChange}>
          Change
        </button>
      )}
    </div>
  );
}

function LotCount({ productId }: { productId: string }) {
  const batches = useQuery({
    queryKey: ["batches", productId],
    queryFn: () => api.batches(productId),
  });
  if (batches.data === undefined) return null;
  const n = batches.data.batches.length;
  return <> · {n === 1 ? "1 lot on file" : `${String(n)} lots on file`}</>;
}

export function ProductNotFound({
  query,
  onSearchAgain,
}: {
  query: string;
  onSearchAgain: () => void;
}) {
  return (
    <Surface tone="neutral" emphasis="filled" data-recall-state="product-not-found">
      <strong>We can&apos;t find that product</strong>
      <p>
        Nothing in your catalogue matches{" "}
        <span className="pp-recall-flow__code">{query}</span>.
      </p>
      <p>{NOT_FOUND_MECHANISM}</p>
      <div className="pp-recall-flow__actions">
        <Button variant="secondary" onClick={onSearchAgain}>
          Search again
        </Button>
        <Link to="/products/new" className="pp-recall-flow__link">
          Add this product
        </Link>
      </div>
    </Surface>
  );
}

export function ProductStep({
  typed,
  query,
  results,
  onType,
  onPick,
}: {
  typed: string;
  query: string;
  results: readonly (ProductSummary & { matched_lot?: string | null })[] | undefined;
  onType: (value: string) => void;
  onPick: (product: ProductSummary) => void;
}) {
  return (
    <section className="pp-recall-flow__body" data-recall-step="product">
      <StepHeader step="Step 1 of 3">Which product is affected?</StepHeader>
      <input
        type="search"
        className="pp-products__search pp-recall-flow__search"
        value={typed}
        placeholder="Search by name, GTIN, SKU or lot code"
        aria-label="Search by name, GTIN, SKU or lot code"
        onChange={(e) => onType(e.target.value)}
      />
      {query === "" || results === undefined ? null : results.length === 0 ? (
        <ProductNotFound query={query} onSearchAgain={() => onType("")} />
      ) : (
        <ul className="pp-recall-flow__choices">
          {results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className="pp-recall-flow__choice"
                onClick={() => onPick(product)}
              >
                <span>{displayName(product.name, product.gtin14).text}</span>
                <span className="pp-recall-flow__meta">
                  <span className="pp-recall-flow__code">{product.gtin14}</span>
                  {product.matched_lot == null ? null : (
                    <span data-matched-lot={product.matched_lot}>
                      {" · "}
                      <span className="pp-recall-flow__code">{product.matched_lot}</span>
                      {" · lot"}
                    </span>
                  )}
                  <LotCount productId={product.id} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ScopeStep({
  product,
  batches,
  form,
  issue,
  onChange,
  onChangeProduct,
  onNext,
}: {
  product: Chosen;
  batches: readonly BatchSummary[];
  form: RecallForm;
  issue: string | null;
  onChange: (form: RecallForm) => void;
  onChangeProduct: () => void;
  onNext: () => void;
}) {
  const lots = splitLots(form.lotText);
  const fresh = form.scope === "lots" ? lotsNotOnFile(lots, batches) : [];
  const toggle = (lot: string) => {
    const next = lots.includes(lot) ? lots.filter((l) => l !== lot) : [...lots, lot];
    onChange({ ...form, scope: "lots", lotText: next.join("\n") });
  };

  return (
    <section className="pp-recall-flow__body" data-recall-step="scope">
      <StepHeader step="Step 2 of 3">Which units are affected?</StepHeader>
      <ProductLine product={product} onChange={onChangeProduct} />

      <label className="pp-recall-flow__option">
        <input
          type="radio"
          name="recall-scope"
          checked={form.scope === "lots"}
          onChange={() => onChange({ ...form, scope: "lots" })}
        />
        <span>Specific lots</span>
      </label>
      {form.scope !== "lots" ? null : (
        <div className="pp-recall-flow__lots">
          <label className="pp-recall-flow__field">
            <span>
              Type or paste the lot codes as they&apos;re printed. One per line, or
              comma-separated.
            </span>
            <textarea
              className="pp-recall-flow__lot-input"
              value={form.lotText}
              rows={3}
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              onChange={(e) => onChange({ ...form, lotText: e.target.value })}
            />
          </label>
          {batches.length === 0 ? null : (
            <div className="pp-recall-flow__chips" aria-label="From your batch records">
              {batches.map((batch) => (
                <button
                  key={batch.id}
                  type="button"
                  className="pp-recall-flow__chip"
                  aria-pressed={lots.includes(batch.lot)}
                  onClick={() => toggle(batch.lot)}
                >
                  {batch.lot}
                  {batch.expiry === null ? null : ` · expiry ${batch.expiry}`}
                </button>
              ))}
            </div>
          )}
          <p className="pp-recall-flow__warning" data-recall-warning="exact-match">
            {EXACT_MATCH_WARNING}
          </p>
          {fresh.length === 0 ? null : (
            <Surface tone="neutral" emphasis="flat" data-recall-state="lot-not-on-file">
              <p>
                {fresh.map((lot) => (
                  <span key={lot} className="pp-recall-flow__code">
                    {lot}{" "}
                  </span>
                ))}
                <strong>New to us</strong>
              </p>
              <p>{NEW_TO_US}</p>
              <p>{CARVE_OUT}</p>
            </Surface>
          )}
        </div>
      )}

      <label className="pp-recall-flow__option">
        <input
          type="radio"
          name="recall-scope"
          checked={form.scope === "gtin"}
          onChange={() => onChange({ ...form, scope: "gtin" })}
        />
        <span>The whole product</span>
      </label>
      <p className="pp-recall-flow__note">
        Every unit of this GTIN, whatever lot it carries.
      </p>

      <div className="pp-recall-flow__nudge">
        <strong>Not sure which lots?</strong>
        <p>{NOT_SURE}</p>
        <Button variant="secondary" onClick={() => onChange({ ...form, scope: "gtin" })}>
          Recall the whole product
        </Button>
      </div>

      {issue === null ? null : (
        <Surface tone="error" emphasis="filled">
          <p>{issue}</p>
        </Surface>
      )}
      <p className="pp-recall-flow__note">
        Nothing is published until you&apos;ve seen the confirmation.
      </p>
      <div className="pp-recall-flow__actions">
        <Button variant="primary" onClick={onNext}>
          Next — write the notice
        </Button>
      </div>
    </section>
  );
}

export function NoticeStep({
  product,
  affected,
  form,
  onChange,
  onBack,
  onNext,
}: {
  product: Chosen;
  affected: string;
  form: RecallForm;
  onChange: (form: RecallForm) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <section className="pp-recall-flow__body" data-recall-step="notice">
      <StepHeader step="Step 3 of 3">Write the notice</StepHeader>
      <ProductLine product={product} />
      <div
        className="pp-recall-flow__tabs"
        role="group"
        aria-label="Where the notice lives"
      >
        <button
          type="button"
          className="pp-recall-flow__tab"
          aria-pressed={form.tab === "builtIn"}
          onClick={() => onChange({ ...form, tab: "builtIn" })}
        >
          Write the notice here
        </button>
        <button
          type="button"
          className="pp-recall-flow__tab"
          aria-pressed={form.tab === "external"}
          onClick={() => onChange({ ...form, tab: "external" })}
        >
          Link to your own page
        </button>
      </div>

      {form.tab === "builtIn" ? (
        <>
          {NOTICE_FIELDS.map(({ key, label }) => (
            <label key={key} className="pp-recall-flow__field">
              <span>{label}</span>
              <textarea
                className="pp-recall-flow__text"
                value={form.fields[key]}
                rows={key === "contact" ? 2 : 3}
                onChange={(e) =>
                  onChange({ ...form, fields: { ...form.fields, [key]: e.target.value } })
                }
              />
            </label>
          ))}
          <div className="pp-recall-flow__fixed" data-recall-field="affectedProducts">
            <span>Affected product</span>
            <p>{affected}</p>
          </div>
          <p className="pp-recall-flow__note">
            Affected product identification — name, GTIN, lot and expiry — is filled in
            from steps 1 and 2 and shown in a fixed format you can&apos;t change.
          </p>
        </>
      ) : (
        <Input
          label="Your notice's address"
          value={form.externalUrl}
          type="url"
          inputMode="url"
          autoComplete="off"
          onChange={(e) => onChange({ ...form, externalUrl: e.target.value })}
        />
      )}

      <div className="pp-recall-flow__actions">
        <Button variant="primary" onClick={onNext}>
          Next — check it
        </Button>
        <button type="button" className="pp-recall-flow__link" onClick={onBack}>
          Back
        </button>
      </div>
    </section>
  );
}

export function Confirmation({
  product,
  form,
  batches,
  affected,
  problem,
  runningRecallId,
  running,
  onChange,
  onBack,
  onPublish,
}: {
  product: Chosen;
  form: RecallForm;
  batches: readonly BatchSummary[];
  affected: string;
  problem: { title: string; detail: string; alreadyActive?: boolean } | null;
  runningRecallId?: string | null | undefined;
  running: boolean;
  onChange: (form: RecallForm) => void;
  onBack: () => void;
  onPublish: () => void;
}) {
  const lots = form.scope === "lots" ? splitLots(form.lotText) : [];
  const fresh = lotsNotOnFile(lots, batches);
  const onFile = lots.length - fresh.length;

  return (
    <section className="pp-recall-flow__body" data-recall-step="confirm">
      <StepHeader step="Step 3 of 3 · nothing published yet">
        Check this before it goes live
      </StepHeader>
      <ProductLine product={product} />

      <div className="pp-recall-flow__label">What this does</div>
      <p className="pp-recall-flow__consequence" data-recall-sentence="scope">
        {scopeSentence(form.scope, lots)}
      </p>
      {form.scope === "gtin" ? null : (
        <p className="pp-recall-flow__note">
          {onFile === lots.length
            ? "Every lot named is on your batch records."
            : `${String(onFile)} of ${String(lots.length)} lots named are on your batch records. ` +
              "A record is created for each of the others when you publish."}
        </p>
      )}

      <label className="pp-recall-flow__check">
        <input
          type="checkbox"
          checked={form.overrideDefault}
          onChange={(e) => onChange({ ...form, overrideDefault: e.target.checked })}
        />
        <span>
          <strong>{OVERRIDE_LABEL}</strong>
          <span
            className="pp-recall-flow__note"
            data-recall-override={form.overrideDefault ? "on" : "off"}
          >
            {form.overrideDefault ? OVERRIDE_ON : OVERRIDE_OFF}
          </span>
        </span>
      </label>

      {form.scope === "gtin" ? null : (
        <p className="pp-recall-flow__warning" data-recall-warning="exact-match">
          {EXACT_MATCH_WARNING}
        </p>
      )}

      <div className="pp-recall-flow__preview" data-recall-preview="dashboard">
        <div className="pp-recall-flow__label">What the notice will say</div>
        {form.tab === "external" ? (
          <p>
            Scans go to your own page:{" "}
            <span className="pp-recall-flow__code">{form.externalUrl}</span>
          </p>
        ) : (
          <dl>
            {NOTICE_FIELDS.filter(({ key }) => form.fields[key].trim() !== "").map(
              ({ key, label }) => (
                <div key={key}>
                  <dt>{label}</dt>
                  <dd>{form.fields[key]}</dd>
                </div>
              ),
            )}
            <div>
              <dt>Affected product</dt>
              <dd>{affected}</dd>
            </div>
          </dl>
        )}
      </div>

      {problem === null ? null : (
        <Surface tone="error" emphasis="filled" data-recall-state="refused">
          <strong>{problem.title}</strong>
          <p>{problem.detail}</p>
          {problem.alreadyActive !== true ||
          runningRecallId === null ||
          runningRecallId === undefined ? null : (
            <Link
              to="/recalls/$recallId"
              params={{ recallId: runningRecallId }}
              className="pp-recall-flow__link"
            >
              Correct the scope
            </Link>
          )}
        </Surface>
      )}

      <div className="pp-recall-flow__service" data-recall-block="scope-of-service">
        <div className="pp-recall-flow__label">What this does not do</div>
        <p>{SCOPE_OF_SERVICE}</p>
      </div>
      <div className="pp-recall-flow__actions">
        <Button variant="secondary" loading={running} onClick={onPublish}>
          {PUBLISH_LABEL}
        </Button>
        <button type="button" className="pp-recall-flow__link" onClick={onBack}>
          Back to the notice
        </button>
      </div>
    </section>
  );
}

export function Live({
  product,
  outcome,
  address,
  sentence,
}: {
  product: Chosen;
  outcome: PublishOutcome;
  address: string;
  sentence: string;
}) {
  return (
    <section className="pp-recall-flow__body" data-recall-step="live">
      <StepHeader step="Published">Recall notice published</StepHeader>
      <ProductLine product={product} />
      <p className="pp-recall-flow__consequence">{sentence}</p>
      <p>
        Scans of affected units reach the notice at{" "}
        <span className="pp-recall-flow__code" data-recall-address="">
          {address}
        </span>{" "}
        within about five seconds.
      </p>
      {outcome.notResolving === null ? null : (
        <Surface tone="warn" emphasis="filled" data-recall-warning="RECALL_NOT_RESOLVING">
          <strong>{outcome.notResolving.title}</strong>
          <p>{outcome.notResolving.detail}</p>
          <Link
            to="/products/$productId"
            params={{ productId: product.id }}
            className="pp-recall-flow__link"
          >
            Publish the product
          </Link>
        </Surface>
      )}
      {outcome.otherWarnings.map((w) => (
        <Surface key={w.code} tone="warn" emphasis="filled" data-recall-warning={w.code}>
          <strong>{w.title}</strong>
          <p>{w.detail}</p>
        </Surface>
      ))}
      <div className="pp-recall-flow__actions">
        <Link
          to="/recalls/$recallId"
          params={{ recallId: outcome.recallId }}
          className="pp-recall-flow__link"
        >
          Open the recall
        </Link>
        <Link to="/" className="pp-recall-flow__link">
          Back to the dashboard
        </Link>
      </div>
    </section>
  );
}

export function RecallNew({ productId }: { productId?: string | undefined }): ReactNode {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(productId === undefined ? "product" : "scope");
  const [form, setForm] = useState<RecallForm>({
    ...EMPTY_RECALL_FORM,
    productId: productId ?? "",
  });
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [issue, setIssue] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQ(typed.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const search = useQuery({
    queryKey: ["products", "list", "recall-search", q],
    queryFn: () => api.products({ q }),
    enabled: q !== "",
  });
  const product = useQuery({
    queryKey: ["products", "read", form.productId],
    queryFn: () => api.product(form.productId),
    enabled: form.productId !== "",
  });
  const batches = useQuery({
    queryKey: ["batches", form.productId],
    queryFn: () => api.batches(form.productId),
    enabled: form.productId !== "",
  });
  const recalls = useQuery({ queryKey: ["recalls"], queryFn: () => api.recalls() });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  const chosen = product.data === undefined ? null : chosenOf(product.data);
  const batchList = batches.data?.batches ?? [];
  const lots = form.scope === "lots" ? splitLots(form.lotText) : [];
  const affected =
    chosen === null
      ? ""
      : affectedProducts(chosen.name, chosen.gtin14, form.scope, lots, batchList);
  const running = activeRecallOn(recalls.data?.recalls ?? [], form.productId);
  const active = running !== undefined;

  const publish = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.publishRecall(body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["recalls"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      setOutcome(publishOutcome(response));
      setStep("live");
    },
    // A cached list may predate the running recall the refusal names.
    onError: () => void queryClient.invalidateQueries({ queryKey: ["recalls"] }),
  });

  const change = (next: RecallForm) => {
    setIssue(null);
    publish.reset();
    setForm(next);
  };

  let body: ReactNode;
  if (step === "product" || chosen === null) {
    body =
      step !== "product" && product.isPending ? (
        <p>Loading&hellip;</p>
      ) : (
        <ProductStep
          typed={typed}
          query={q}
          results={search.data?.products}
          onType={setTyped}
          onPick={(picked) => {
            change({ ...form, productId: picked.id, lotText: "" });
            setStep("scope");
          }}
        />
      );
  } else if (step === "scope") {
    body = (
      <ScopeStep
        product={chosen}
        batches={batchList}
        form={form}
        issue={issue}
        onChange={change}
        onChangeProduct={() => setStep("product")}
        onNext={() => {
          const found = active ? null : preflight(toPublishBody(form, affected), false);
          setIssue(found);
          if (found === null) setStep("notice");
        }}
      />
    );
  } else if (step === "notice") {
    body = (
      <NoticeStep
        product={chosen}
        affected={affected}
        form={form}
        onChange={change}
        onBack={() => setStep("scope")}
        onNext={() => setStep("confirm")}
      />
    );
  } else if (step === "confirm") {
    const refusal = publish.error === null ? null : refusalView(problemOf(publish.error));
    body = (
      <Confirmation
        product={chosen}
        form={form}
        batches={batchList}
        affected={affected}
        problem={
          issue === null
            ? refusal
            : { title: "This notice cannot be published", detail: issue }
        }
        runningRecallId={running?.id ?? null}
        running={publish.isPending}
        onChange={change}
        onBack={() => setStep("notice")}
        onPublish={() => {
          const request = toPublishBody(form, affected);
          const found = preflight(request, active);
          setIssue(found);
          if (found === null) publish.mutate(request);
        }}
      />
    );
  } else {
    body =
      outcome === null ? null : (
        <Live
          product={chosen}
          outcome={outcome}
          sentence={scopeSentence(form.scope, lots)}
          address={noticeAddress(
            org.data?.resolverBaseUrl ?? "",
            recallNoticePath(chosen.gtin14, outcome.recallId),
            form.externalUrl,
            form.tab,
          )}
        />
      );
  }

  return (
    <RecallFlow>
      <div data-screen-label="Publish a recall notice" className="pp-recall-flow">
        <Surface>{body}</Surface>
      </div>
    </RecallFlow>
  );
}
